import { IconPlus, IconSearch } from "@tabler/icons-react";
import { type Ref, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import type { ProductInventoryGate } from "./product-listing-helpers";
import { ResolutionRow } from "./ResolutionRow";
import type { ProductListingBrowse, ProductStatusFilter } from "./use-product-listing-browse";
import type { ProductResolution } from "./use-product-resolution";

interface ProductBrowseToolbarProps {
  browse: Pick<
    ProductListingBrowse,
    | "search"
    | "availability"
    | "productStatus"
    | "changeSearch"
    | "changeAvailability"
    | "changeProductStatus"
  >;
  showAvailabilityFilter: boolean;
  /** Single-SKU pickers (template portfolio) only search; they have no bulk entry. */
  allowBulkEntry?: boolean;
  bulkOpen: boolean;
  onBulkOpenChange: (open: boolean) => void;
  enterInput: string;
  resolution: ProductResolution;
  selectedBySku: Map<string, SelectedProduct>;
  bulkPending: boolean;
  listingAdProduct: "sp" | undefined;
  inventory: ProductInventoryGate;
  /** Lets the single-SKU panel's "Change" action return focus to the search box. */
  searchInputRef?: Ref<HTMLInputElement>;
  onAdd: (product: SelectedProduct) => void;
}

/** Search and filter controls plus the bulk SKU/ASIN entry popover. */
export function ProductBrowseToolbar({
  browse,
  showAvailabilityFilter,
  allowBulkEntry = true,
  bulkOpen,
  onBulkOpenChange,
  enterInput,
  resolution,
  selectedBySku,
  bulkPending,
  listingAdProduct,
  inventory,
  searchInputRef,
  onAdd,
}: ProductBrowseToolbarProps) {
  const { t } = useTranslation("campaignCreation");
  const bulkInputRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <InputGroup className="h-8 min-w-[220px] flex-1">
        <InputGroupAddon align="inline-start">
          <IconSearch className="size-4" aria-hidden="true" />
        </InputGroupAddon>
        <InputGroupInput
          ref={searchInputRef}
          size="sm"
          value={browse.search}
          onChange={(event) => browse.changeSearch(event.target.value)}
          className="h-full"
          placeholder={t("products.searchPlaceholder")}
          aria-label={t("products.searchPlaceholder")}
        />
      </InputGroup>
      {showAvailabilityFilter ? (
        <Select
          items={[
            { value: null, label: t("products.availability.all") },
            { value: "IN_STOCK", label: t("products.availability.inStock") },
            { value: "IN_STOCK_SCARCE", label: t("products.availability.scarce") },
            { value: "OUT_OF_STOCK", label: t("products.availability.outOfStock") },
            { value: "unknown", label: t("products.availability.unknown") },
          ]}
          value={browse.availability}
          onValueChange={browse.changeAvailability}
        >
          <SelectTrigger aria-label={t("products.availability.label")} className="w-44" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={null}>{t("products.availability.all")}</SelectItem>
              {[
                ["IN_STOCK", t("products.availability.inStock")],
                ["IN_STOCK_SCARCE", t("products.availability.scarce")],
                ["OUT_OF_STOCK", t("products.availability.outOfStock")],
                ["unknown", t("products.availability.unknown")],
              ].map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      ) : null}
      <Select
        items={[
          { value: null, label: t("products.status.all") },
          { value: "advertised", label: t("products.status.advertised") },
          { value: "unadvertised", label: t("products.status.unadvertised") },
        ]}
        value={browse.productStatus}
        onValueChange={(value) => browse.changeProductStatus(value as ProductStatusFilter)}
      >
        <SelectTrigger aria-label={t("products.status.label")} className="w-44" size="sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={null}>{t("products.status.all")}</SelectItem>
            <SelectItem value="advertised">{t("products.status.advertised")}</SelectItem>
            <SelectItem value="unadvertised">{t("products.status.unadvertised")}</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {allowBulkEntry ? (
        <Popover open={bulkOpen} onOpenChange={onBulkOpenChange}>
          <PopoverTrigger
            render={<Button type="button" variant="outline" size="sm" className="ml-auto" />}
          >
            <IconPlus data-icon="inline-start" />
            {t("products.bulk.open")}
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="w-[420px] max-w-[calc(100vw-32px)] [&>[data-slot=popover-viewport]]:pb-3"
            initialFocus={bulkInputRef}
          >
            <div className="flex flex-col gap-3">
              <PopoverTitle className="sr-only">{t("products.bulk.open")}</PopoverTitle>
              <label htmlFor="campaign-products-enter" className="text-sm font-medium">
                {t("products.enter.label")}
              </label>
              <Textarea
                id="campaign-products-enter"
                ref={bulkInputRef}
                value={enterInput}
                onChange={(event) => resolution.changeEnterInput(event.target.value)}
                className="min-h-32 resize-y"
                placeholder={t("products.enter.placeholder")}
              />
              <div className="flex justify-end gap-2">
                <PopoverClose render={<Button type="button" variant="outline" size="sm" />}>
                  {t("products.bulk.close")}
                </PopoverClose>
                <Button
                  type="button"
                  size="sm"
                  disabled={!enterInput.trim() || resolution.resolvePending || bulkPending}
                  loading={resolution.resolveLoadingVisible}
                  onClick={resolution.resolveEnteredProducts}
                >
                  {t("products.enter.resolve")}
                </Button>
              </div>
              {resolution.resolveFailed ? (
                <ErrorState
                  message={t("products.loadError")}
                  onRetry={resolution.resolveEnteredProducts}
                />
              ) : null}
              {resolution.resolveInputError ? (
                <p role="alert" className="text-sm text-destructive">
                  {resolution.resolveInputError}
                </p>
              ) : null}
              {resolution.resolutionRows.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {resolution.resolutionRows.map(({ key, resolution: row }) => (
                    <ResolutionRow
                      key={key}
                      resolution={row}
                      selectedSkus={selectedBySku}
                      addingDisabled={bulkPending}
                      retryPending={resolution.resolvePending}
                      onRetry={resolution.resolveEnteredProducts}
                      adProduct={listingAdProduct}
                      inventory={inventory}
                      onAdd={onAdd}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
